import React, { useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { CLAIM_STEPS } from '../data/products';
import { PHONE_TEL, WHATSAPP } from '../data/contact';
import Reveal from '../components/layout/Reveal';
import Faq from '../components/layout/Faq';
import { Breadcrumb, usePageTitle } from '../components/layout/PageBits';
import LeadForm from '../components/product/LeadForm';
import { HealthCoverTool, LifeCoverTool } from '../components/product/CoverTools';

const DOTS = { backgroundImage: 'radial-gradient(rgba(15,27,61,.09) 1px, transparent 1px)', backgroundSize: '18px 18px' };

function ClaimSteps({ product }) {
  const { lang, t } = useLang();
  const [kind, setKind] = useState('cashless');
  const steps = product.claim === 'health' ? CLAIM_STEPS.health[lang][kind] : CLAIM_STEPS.general[lang];
  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <h2 className="h2">{t('claimTitle')}</h2>
          {product.claim === 'health' && (
            <div className="inline-flex bg-slate-100 rounded-xl p-1 text-sm font-semibold self-start" role="tablist">
              {['cashless', 'reimb'].map((k) => (
                <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => setKind(k)}
                  className={`px-4 py-2 rounded-lg transition ${kind === k ? 'bg-white shadow text-ink' : 'text-slate-500'}`}>{t(k)}</button>
              ))}
            </div>
          )}
        </Reveal>
        <ol className={`relative border-l-2 ml-4 space-y-7 ${product.tone.line}`}>
          {steps.map(([h, p], i) => (
            <li key={h} className="pl-8 relative">
              <span className={`absolute -left-[17px] top-0 w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center ring-4 ring-white ${product.tone.dot}`}>{i + 1}</span>
              <h3 className="font-bold text-lg">{h}</h3><p className="text-slate-600 mt-1">{p}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export default function InsuranceProductPage({ product }) {
  const { lang, t } = useLang();
  const c = product[lang];
  usePageTitle(c.title);
  return (
    <>
      <section className={`${product.tone.soft} border-b border-slate-100`} style={DOTS}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <Reveal className="relative">
            <img src={product.img} alt="" aria-hidden="true" className="hidden lg:block absolute -top-2 right-0 w-28 xl:w-32 drop-shadow-xl pointer-events-none" />
            <Breadcrumb current={c.title} />
            <span className={`inline-flex items-center gap-2 bg-white text-xs font-bold rounded-full px-3 py-1.5 shadow-sm ${product.tone.text}`}>{product.emoji} {c.title}</span>
            <h1 className="mt-4 text-3xl sm:text-5xl font-extrabold leading-tight tracking-tight">{c.heading}</h1>
            <p className="mt-4 text-lg text-slate-600">{c.sub}</p>
            <ul className="mt-6 space-y-2.5 text-slate-700">
              {c.bullets.map((b) => <li key={b} className="flex gap-3"><span className={`w-6 h-6 rounded-full text-white text-xs flex items-center justify-center shrink-0 ${product.tone.dot}`}>✓</span>{b}</li>)}
            </ul>
          </Reveal>
          <Reveal><LeadForm product={product} /></Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Illustration (stand-in): swap public/img/scene-*.webp for a real photo any time */}
          <Reveal><img src={product.scene} alt="" aria-hidden="true" loading="lazy" decoding="async" className="w-full h-48 sm:h-72 lg:h-80 object-cover rounded-3xl shadow-lg mb-12" /></Reveal>
          <Reveal><h2 className="h2 text-center mb-10">{t('covTitle')}</h2></Reveal>
          <Reveal className="grid md:grid-cols-2 gap-5">
            <div className="rounded-3xl border-2 border-emerald-200 p-6"><h3 className="font-bold text-emerald-700 mb-4">{t('covYes')}</h3>
              <ul className="space-y-3">{c.covered.map((x) => <li key={x} className="flex gap-3"><span className="text-emerald-600 font-bold">✓</span>{x}</li>)}</ul></div>
            <div className="rounded-3xl border-2 border-rose-200 p-6"><h3 className="font-bold text-rose-600 mb-4">{t('covNo')}</h3>
              <ul className="space-y-3">{c.notCovered.map((x) => <li key={x} className="flex gap-3"><span className="text-rose-500 font-bold">✗</span>{x}</li>)}</ul></div>
          </Reveal>
          <p className="mt-4 text-center text-sm text-slate-500">{t('covNote')}</p>
        </div>
      </section>

      {product.tool && (
        <section className="py-16 sm:py-20 bg-sky">
          <Reveal className="max-w-5xl mx-auto px-4 sm:px-6">{product.tool === 'health' ? <HealthCoverTool /> : <LifeCoverTool />}</Reveal>
        </section>
      )}

      <ClaimSteps product={product} />

      <section className="py-16 sm:py-20 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal><h2 className="h2 text-center mb-8">{t('faqShort')}</h2></Reveal>
          <Reveal><Faq items={c.faq} accent={product.tone.text} divide="divide-amber-200/60 border-amber-200/60" /></Reveal>
        </div>
      </section>

      {/* Desktop "human touch" bar */}
      <div className="hidden md:flex fixed bottom-4 left-1/2 -translate-x-1/2 z-30 items-center gap-4 bg-white rounded-2xl shadow-2xl border border-slate-200 px-3 py-2.5">
        <div className="w-11 h-11 rounded-full photo-slot border-2 border-white shadow" aria-hidden="true" />
        <div className="pr-2"><div className="font-bold text-sm">{t('needHuman')}</div><div className="text-xs text-slate-500">{t('talk')}</div></div>
        <a href={PHONE_TEL} className="bg-ink text-white text-sm font-bold px-4 py-2 rounded-xl">📞 {t('call')}</a>
        <a href={WHATSAPP} className="bg-emerald-500 text-white text-sm font-bold px-4 py-2 rounded-xl">💬 WhatsApp</a>
      </div>
      <div className="hidden md:block h-20" aria-hidden="true" />
    </>
  );
}
